import { useEffect, useReducer, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, CircularProgress, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import CategoriesGrid from './CategoriesGrid';
import ProductsGrid from './ProductsGrid';
import ModifierGroupsPanel from './ModifierGroupsPanel';
import OrderPanel from './OrderPanel';
import ActionBar from './ActionBar';
import NestedLayoutModal from './NestedLayoutModal';
import {
  addItemToPreviewOrder,
  removeItemFromPreviewOrder,
  validateAllModifierGroups,
  removeNestedGroupsForPrefix,
  buildOptionPathKey,
  isMultiplierConditionActive,
  getEffectiveOptionsCount,
  extractParentOptionPathKey,
  getEffectivePriceType,
  calculatePriceByType,
  PRICE_TYPES,
  applyProductPreselections,
  applyOptionPreselections,
  recalculateFreeForGroups,
  applyEffectiveAQ,
  applyZeroPricesForMatpParents,
} from './previewHelpers';
import { fetchMenuMap, fetchMenuId } from '../../../redux/actions/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';
import { POS_COLORS } from './posColors';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
const initialState = {
  selectedCategory: null,
  selectedProduct: null,
  modifierGroups: {}, // { [pathKey]: { ...mgDef, modifierOptions: { [optId]: { ...item, count, price, ... } } } }
  count: 1,
  orderData: null, // OrderDto from SDK

  // Navigation state (mirrors POS client)
  pendingOptionItem: null, // The item whose modifier groups are currently displayed
  currentOptionPathKey: null, // Full pathKey of the option we're inside (null = product level)
  parentOptionItemStack: [], // [{ modifierGroupId, option }]
};

const A = {
  SELECT_CATEGORY: 'SELECT_CATEGORY',
  SELECT_PRODUCT: 'SELECT_PRODUCT',
  SET_MODIFIER_GROUPS: 'SET_MODIFIER_GROUPS',
  SET_COUNT: 'SET_COUNT',
  SET_ORDER_DATA: 'SET_ORDER_DATA',
  RESET_SELECTION: 'RESET_SELECTION',
  CLEAR_ORDER: 'CLEAR_ORDER',
  // Navigation
  NAVIGATE_INTO: 'NAVIGATE_INTO',
  NAVIGATE_UP: 'NAVIGATE_UP',
  NAVIGATE_TO_PRODUCT: 'NAVIGATE_TO_PRODUCT',
  SWITCH_SIBLING: 'SWITCH_SIBLING',
};

function previewReducer(state, action) {
  switch (action.type) {
    case A.SELECT_CATEGORY:
      return {
        ...initialState,
        orderData: state.orderData,
        selectedCategory: action.payload,
      };

    case A.SELECT_PRODUCT:
      return {
        ...state,
        selectedProduct: action.payload,
        modifierGroups: {},
        count: 1,
        pendingOptionItem: null,
        currentOptionPathKey: null,
        parentOptionItemStack: [],
      };

    case A.SET_MODIFIER_GROUPS: {
      const groups = action.payload;

      // Apply effective AQ to all groups with applyValidationToItemsActualQuantity
      // before free recalculation — matching POS reducer behaviour where
      // applyEffectiveAQ runs in every action that modifies options.
      for (const [key, group] of Object.entries(groups)) {
        if (
          group?.applyValidationToItemsActualQuantity &&
          group?.modifierOptions
        ) {
          applyEffectiveAQ(key, group.modifierOptions, true, groups);
        }
      }

      // Recalculate free options (before sorting) so the SDK sees
      // options in selection order — matching POS client reducer behaviour.
      const withFree = recalculateFreeForGroups(groups);

      const withZeroedParents = applyZeroPricesForMatpParents(withFree);

      // THEN sort each group's modifierOptions by menu-defined order (optionIds)
      // so they appear in the item's configured order for display.
      const sorted = {};
      for (const [key, group] of Object.entries(withZeroedParents)) {
        const optionIds = group.optionIds || [];
        const opts = group.modifierOptions || {};
        if (
          optionIds.length > 0 &&
          typeof opts === 'object' &&
          !Array.isArray(opts)
        ) {
          const sortedOpts = {};
          for (const id of optionIds) {
            if (opts[id] !== undefined) {
              sortedOpts[id] = opts[id];
            }
          }
          for (const id of Object.keys(opts)) {
            if (sortedOpts[id] === undefined) {
              sortedOpts[id] = opts[id];
            }
          }
          sorted[key] = { ...group, modifierOptions: sortedOpts };
        } else {
          sorted[key] = group;
        }
      }
      return { ...state, modifierGroups: sorted };
    }

    case A.SET_COUNT:
      return { ...state, count: action.payload };

    case A.SET_ORDER_DATA:
      return { ...state, orderData: action.payload };

    case A.RESET_SELECTION:
      return {
        ...state,
        selectedProduct: null,
        modifierGroups: {},
        count: 1,
        pendingOptionItem: null,
        currentOptionPathKey: null,
        parentOptionItemStack: [],
      };

    case A.CLEAR_ORDER:
      return { ...initialState };

    // Push current pending onto stack, set new pending + pathKey
    case A.NAVIGATE_INTO: {
      const { optionItem, modifierGroupId, optionPathKey } = action.payload;
      const newStack = [...state.parentOptionItemStack];
      // Only push if current pending is NOT the product itself
      if (
        state.pendingOptionItem &&
        state.pendingOptionItem._id !== state.selectedProduct?._id
      ) {
        newStack.push({
          modifierGroupId: state.pendingOptionItem._mgId || modifierGroupId,
          option: state.pendingOptionItem,
        });
      }
      return {
        ...state,
        pendingOptionItem: { ...optionItem, _mgId: modifierGroupId },
        currentOptionPathKey: optionPathKey,
        parentOptionItemStack: newStack,
      };
    }

    // Pop stack, restore previous pending + derive parent pathKey
    case A.NAVIGATE_UP: {
      const stack = [...state.parentOptionItemStack];
      if (stack.length > 0) {
        const previous = stack.pop();
        const parentPK = extractParentOptionPathKey(state.currentOptionPathKey);
        return {
          ...state,
          pendingOptionItem: {
            ...previous.option,
            _mgId: previous.modifierGroupId,
          },
          currentOptionPathKey: parentPK,
          parentOptionItemStack: stack,
        };
      }
      // Back to product level
      return {
        ...state,
        pendingOptionItem: null,
        currentOptionPathKey: null,
        parentOptionItemStack: [],
      };
    }

    case A.NAVIGATE_TO_PRODUCT:
      return {
        ...state,
        pendingOptionItem: null,
        currentOptionPathKey: null,
        parentOptionItemStack: [],
      };

    // Switch to a sibling option at the same nesting level (no stack change)
    case A.SWITCH_SIBLING: {
      const { newOptionItem, modifierGroupId, newOptionPathKey } =
        action.payload;
      return {
        ...state,
        pendingOptionItem: { ...newOptionItem, _mgId: modifierGroupId },
        currentOptionPathKey: newOptionPathKey,
        // Stack stays the same – we're at the same depth
      };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PosMenuPreview = () => {
  const reduxDispatch = useDispatch();
  const { menuMap, loading } = useSelector((s) => s.posMenu);
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((s) => s.providers);

  const [state, dispatch] = useReducer(previewReducer, initialState);
  const businessId = JSON.parse(localStorage.getItem('businessInfo'))?.id;
  const providersLoaded = providerId !== null || providerItems?.length > 0;
  const isPosConnected = isPosProviderConnected(providerItems);

  // Are we inside a nested option?
  const isInNestedContext =
    state.pendingOptionItem != null &&
    state.pendingOptionItem._id !== state.selectedProduct?._id;

  // -----------------------------------------------------------------------
  // Nested Layout Modal state
  // -----------------------------------------------------------------------
  const [nestedModal, setNestedModal] = useState({
    open: false,
    optionItem: null,
    parentMgDef: null,
    parentMgPathKey: null,
    optionPathKey: null,
  });

  const handleOpenNestedModal = useCallback((data) => {
    setNestedModal({
      open: true,
      optionItem: data.optionItem,
      parentMgDef: data.parentMgDef,
      parentMgPathKey: data.parentMgPathKey,
      optionPathKey: data.optionPathKey,
    });
  }, []);

  const handleCloseNestedModal = useCallback(() => {
    setNestedModal({
      open: false,
      optionItem: null,
      parentMgDef: null,
      parentMgPathKey: null,
      optionPathKey: null,
    });
  }, []);

  // Fetch menu
  useEffect(() => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }
    reduxDispatch(fetchMenuId(businessId))
      .then((id) => reduxDispatch(fetchMenuMap(id)))
      .catch(() => toast.error('Failed to load menu data'));
  }, [reduxDispatch, businessId]);

  // ------ Handlers -------------------------------------------------------

  const handleSelectCategory = useCallback(
    (category) => dispatch({ type: A.SELECT_CATEGORY, payload: category }),
    []
  );

  const handleSelectProduct = useCallback(
    (product) => {
      dispatch({ type: A.SELECT_PRODUCT, payload: product });

      // Preselected options (mirrors POS: handlePreselectedModifierGroupOption)
      if (
        menuMap &&
        (product.preselectOptions?.defaultSelection ||
          (product.modifierGroupIds && product.modifierGroupIds.length > 0))
      ) {
        const preselectedGroups = applyProductPreselections(product, menuMap);
        if (Object.keys(preselectedGroups).length > 0) {
          dispatch({ type: A.SET_MODIFIER_GROUPS, payload: preselectedGroups });
        }
      }

      // Auto-add if no modifier groups
      if (!product.modifierGroupIds || product.modifierGroupIds.length === 0) {
        try {
          const updatedOrder = addItemToPreviewOrder({
            orderData: state.orderData,
            selectedProduct: product,
            selectedCategory: state.selectedCategory,
            modifierGroups: {},
            count: 1,
            businessId,
          });
          dispatch({ type: A.SET_ORDER_DATA, payload: updatedOrder });
          dispatch({ type: A.RESET_SELECTION });
        } catch (err) {
          console.error('Failed to add item:', err);
          toast.error('Failed to add item to order');
        }
      }
    },
    [state.orderData, state.selectedCategory, businessId, menuMap]
  );

  const handleUpdateModifierGroups = useCallback(
    (groups, context) => {
      let nextGroups = groups;
      if (context?.selectedOptionId && context?.parentMgPathKey && menuMap) {
        nextGroups = applyOptionPreselections(
          state.selectedProduct,
          menuMap,
          groups,
          context.selectedOptionId,
          context.parentMgPathKey,
          context.selectedOptionData || {}
        );
      }
      dispatch({ type: A.SET_MODIFIER_GROUPS, payload: nextGroups });
    },
    [state.selectedProduct, menuMap]
  );

  const handleSetCount = useCallback((n) => {
    dispatch({ type: A.SET_COUNT, payload: n });
  }, []);

  // Navigation
  const handleNavigateInto = useCallback(
    (optionItem, modifierGroupId, optionPathKey) => {
      dispatch({
        type: A.NAVIGATE_INTO,
        payload: { optionItem, modifierGroupId, optionPathKey },
      });
    },
    []
  );

  const handleNavigateUp = useCallback(() => {
    dispatch({ type: A.NAVIGATE_UP });
  }, []);

  // ------ Switch Sibling Option (port of POS client handleSwitchSiblingOption) ------
  const handleSwitchSiblingOption = useCallback(
    (newOptionId, parentModifierGroupId) => {
      if (!state.selectedProduct || !menuMap) return;

      const currentOptionId = state.pendingOptionItem?._id;
      if (currentOptionId === newOptionId) return; // Same option, no switch

      const newOption = menuMap?.items?.[newOptionId];
      const parentMg = menuMap?.modifierGroups?.[parentModifierGroupId];
      if (!newOption || !parentMg) return;

      const switchMultiplierCond = {
        applyConditionWithMultiplier: parentMg.applyConditionWithMultiplier,
        priceType: parentMg.priceType,
      };
      const switchMultiplierActive =
        isMultiplierConditionActive(switchMultiplierCond);

      // Build the parent MG pathKey
      let parentMgPathKey;
      if (state.currentOptionPathKey) {
        const lastOpIndex = state.currentOptionPathKey.lastIndexOf('__op_');
        parentMgPathKey =
          lastOpIndex > 0
            ? state.currentOptionPathKey.substring(0, lastOpIndex)
            : `product_${state.selectedProduct._id}__mg_${parentModifierGroupId}`;
      } else {
        parentMgPathKey = `product_${state.selectedProduct._id}__mg_${parentModifierGroupId}`;
      }

      let updatedGroups = { ...state.modifierGroups };
      const currentParentMgSelection = updatedGroups[parentMgPathKey];
      const modifierOptions = currentParentMgSelection?.modifierOptions || {};

      const hasNestedModifiers =
        newOption.modifierGroupIds && newOption.modifierGroupIds.length > 0;

      const alreadySelected = !!modifierOptions[newOptionId];

      const switchShouldReplaceAll = (() => {
        if (switchMultiplierActive && parentMg.max) {
          const optionsForCount = alreadySelected
            ? Object.values(modifierOptions).filter(
                (o) => o?._id !== newOptionId
              )
            : Object.values(modifierOptions);
          const currentEffective = getEffectiveOptionsCount(
            optionsForCount,
            parentMg.applyValidationToItemsActualQuantity,
            switchMultiplierCond
          );
          const newWeight = newOption.originalPrice ?? newOption.price ?? 0;
          return currentEffective + newWeight > parentMg.max;
        }
        return parentMg.max === 1;
      })();

      if (switchShouldReplaceAll) {
        Object.keys(modifierOptions).forEach((oldOptId) => {
          if (oldOptId === newOptionId) return;
          const oldBranch = buildOptionPathKey(parentMgPathKey, oldOptId);
          updatedGroups = removeNestedGroupsForPrefix(updatedGroups, oldBranch);
        });
        const cleanedOpts = {};
        if (alreadySelected) {
          cleanedOpts[newOptionId] = modifierOptions[newOptionId];
        }
        if (Object.keys(cleanedOpts).length === 0) {
          delete updatedGroups[parentMgPathKey];
        } else {
          updatedGroups[parentMgPathKey] = {
            ...(updatedGroups[parentMgPathKey] || currentParentMgSelection),
            modifierOptions: cleanedOpts,
          };
        }
      }

      // Calculate price for the new option
      let parentOptionInfo = null;
      let isNestedUnderParentOption = false;

      if (state.parentOptionItemStack.length > 0) {
        const parentStackItem =
          state.parentOptionItemStack[state.parentOptionItemStack.length - 1];
        const parentOptionId = parentStackItem?.option?._id;
        if (parentOptionId) {
          const menuParentOption = menuMap?.items?.[parentOptionId];
          parentOptionInfo = {
            _id: parentOptionId,
            ...menuParentOption,
            originalPrice: menuParentOption?.price ?? 0,
            priceType: menuParentOption?.priceType ?? null,
          };
          isNestedUnderParentOption = true;
        }
      }

      const priceResult = calculatePriceByType({
        option: {
          ...newOption,
          priceType: newOption?.priceType,
        },
        modifierGroup: parentMg,
        parentOption: parentOptionInfo,
        isNestedUnderParentOption,
      });

      // Add new option to parent MG selection
      const newEntry = {
        ...newOption,
        count: 1,
        price: priceResult.optionPrice,
        originalPrice: priceResult.originalPrice,
        calculatedPrice: priceResult.calculatedPrice,
        priceType: priceResult.effectivePriceType,
        actualQuantityWithMultiplier: priceResult.actualQuantityWithMultiplier,
        calculatedActualQuantity: priceResult.actualQuantityWithMultiplier,
        hideNameOnOrder: newOption?.hideNameOnOrder,
        inOrderViewLayout: newOption?.inOrderViewLayout,
      };

      const newModifierOptions = switchShouldReplaceAll
        ? {
            [newOptionId]: alreadySelected
              ? modifierOptions[newOptionId]
              : newEntry,
          }
        : {
            ...(updatedGroups[parentMgPathKey]?.modifierOptions || {}),
            [newOptionId]: alreadySelected
              ? modifierOptions[newOptionId]
              : newEntry,
          };

      updatedGroups[parentMgPathKey] = {
        ...parentMg,
        pathKey: parentMgPathKey,
        hideNameOnOrder:
          menuMap?.modifierGroups?.[parentMg._id]?.hideNameOnOrder ??
          parentMg.hideNameOnOrder,
        inOrderViewLayout:
          menuMap?.modifierGroups?.[parentMg._id]?.inOrderViewLayout ??
          parentMg.inOrderViewLayout,
        modifierOptions: newModifierOptions,
      };

      if (hasNestedModifiers && !alreadySelected) {
        updatedGroups = applyOptionPreselections(
          state.selectedProduct,
          menuMap,
          updatedGroups,
          newOptionId,
          parentMgPathKey,
          newEntry
        );
      }

      dispatch({ type: A.SET_MODIFIER_GROUPS, payload: updatedGroups });

      // Build new option path key
      const newOptionPathKey = `${parentMgPathKey}__op_${newOptionId}`;

      if (hasNestedModifiers) {
        // Stay at nested level – switch to new sibling
        dispatch({
          type: A.SWITCH_SIBLING,
          payload: {
            newOptionItem: newOption,
            modifierGroupId: parentModifierGroupId,
            newOptionPathKey,
          },
        });
      } else {
        // New option has NO nested modifiers – navigate up
        handleNavigateUp();
      }
    },
    [state, menuMap, handleNavigateUp]
  );

  // ------ Sibling options data (for ActionBar SiblingOptionsRow) ----------
  const siblingData = useMemo(() => {
    if (!state.pendingOptionItem || !state.selectedProduct || !menuMap) {
      return null;
    }

    const currentOptionId = state.pendingOptionItem._id;
    const isAtProductLevel = currentOptionId === state.selectedProduct._id;
    if (isAtProductLevel) return null;

    const currentOption = menuMap?.items?.[currentOptionId];
    if (!currentOption) return null;

    // pendingModifierGroupId = the MG that contains the current option
    const pendingModifierGroupId = state.pendingOptionItem._mgId;
    if (!pendingModifierGroupId) return null;

    const parentMg = menuMap?.modifierGroups?.[pendingModifierGroupId];
    if (!parentMg?.optionIds?.length) return null;

    // Only show if current option has multiplierApplyingToNestedPrice
    const currentEffectivePriceType = getEffectivePriceType(
      currentOption,
      parentMg
    );
    if (
      currentEffectivePriceType !==
      PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE
    ) {
      return null;
    }

    // Get all sibling options with the same priceType
    const siblingOptions = parentMg.optionIds
      .map((optionId) => {
        const option = menuMap?.items?.[optionId];
        if (!option) return null;

        const effectivePriceType = getEffectivePriceType(option, parentMg);
        if (
          effectivePriceType !== PRICE_TYPES.MULTIPLIER_APPLYING_TO_NESTED_PRICE
        ) {
          return null;
        }

        const isSoldOut =
          option?.soldOut?.status === 'soldOutPermanent' ||
          (option?.soldOut?.status === 'soldOutToday' &&
            new Date(option?.soldOut?.soldOutDate) >= new Date());

        return {
          _id: optionId,
          name: option.name?.en || option.name || 'Option',
          isUnavailable: isSoldOut,
        };
      })
      .filter(Boolean);

    if (siblingOptions.length <= 1) return null;

    return {
      parentModifierGroupId: pendingModifierGroupId,
      siblingOptions,
      currentOptionId,
    };
  }, [state.pendingOptionItem, state.selectedProduct, menuMap]);

  // "Save & navigate up" – if no nested selections were made, remove the
  // option from its parent MG, then navigate up.
  const handleSaveNavigateUp = useCallback(() => {
    if (!state.pendingOptionItem || !state.currentOptionPathKey) {
      handleNavigateUp();
      return;
    }

    const currentOptId = state.pendingOptionItem._id;
    // Check for nested selections under this option
    const hasNested = Object.keys(state.modifierGroups).some((pk) =>
      pk.startsWith(state.currentOptionPathKey + '__')
    );

    if (!hasNested) {
      // Remove this option from its parent modifier group
      const lastOpIdx = state.currentOptionPathKey.lastIndexOf('__op_');
      if (lastOpIdx > 0) {
        const parentMgPathKey = state.currentOptionPathKey.substring(
          0,
          lastOpIdx
        );
        const updatedGroups = { ...state.modifierGroups };
        const parentMg = updatedGroups[parentMgPathKey];
        if (parentMg?.modifierOptions?.[currentOptId]) {
          const newOpts = { ...parentMg.modifierOptions };
          delete newOpts[currentOptId];
          if (Object.keys(newOpts).length === 0) {
            delete updatedGroups[parentMgPathKey];
          } else {
            updatedGroups[parentMgPathKey] = {
              ...parentMg,
              modifierOptions: newOpts,
            };
          }
          dispatch({ type: A.SET_MODIFIER_GROUPS, payload: updatedGroups });
        }
      }
    }

    handleNavigateUp();
  }, [
    state.pendingOptionItem,
    state.currentOptionPathKey,
    state.modifierGroups,
    handleNavigateUp,
  ]);

  // Done – add to order (works from any level, navigates to product level first)
  const handleDone = useCallback(() => {
    if (!state.selectedProduct) return;

    // Navigate to product level first if in nested context
    if (isInNestedContext) {
      dispatch({ type: A.NAVIGATE_TO_PRODUCT });
    }

    const { valid, missingGroups } = validateAllModifierGroups(
      state.selectedProduct._id,
      state.selectedProduct.modifierGroupIds,
      null,
      state.modifierGroups,
      menuMap
    );

    if (!valid) {
      toast.warn(`Required modifiers: ${missingGroups.join(', ')}`);
      return;
    }

    try {
      const updatedOrder = addItemToPreviewOrder({
        orderData: state.orderData,
        selectedProduct: state.selectedProduct,
        selectedCategory: state.selectedCategory,
        modifierGroups: state.modifierGroups,
        count: state.count,
        businessId,
      });
      dispatch({ type: A.SET_ORDER_DATA, payload: updatedOrder });
      dispatch({ type: A.RESET_SELECTION });
    } catch (err) {
      console.error('Failed to add item:', err);
      toast.error('Failed to add item to order');
    }
  }, [state, menuMap, businessId, isInNestedContext]);

  const handleCancel = useCallback(() => {
    dispatch({ type: A.RESET_SELECTION });
  }, []);

  const handleRemoveItem = useCallback(
    (itemUuid) => {
      const updated = removeItemFromPreviewOrder(state.orderData, itemUuid);
      dispatch({ type: A.SET_ORDER_DATA, payload: updated });
    },
    [state.orderData]
  );

  const handleClearOrder = useCallback(() => {
    dispatch({ type: A.CLEAR_ORDER });
  }, []);

  // Validation for ActionBar
  const { valid: isModifiersValid, missingGroups } = useMemo(
    () =>
      state.selectedProduct
        ? validateAllModifierGroups(
            state.selectedProduct._id,
            state.selectedProduct.modifierGroupIds,
            null,
            state.modifierGroups,
            menuMap
          )
        : { valid: true, missingGroups: [] },
    [state.selectedProduct, state.modifierGroups, menuMap]
  );

  // ------ Loading / guards -----------------------------------------------

  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 100px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 74px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu
            Preview.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (loading && !menuMap) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ------ Layout ---------------------------------------------------------

  const hasModifiers = state.selectedProduct?.modifierGroupIds?.length > 0;

  return (
    <Box
      sx={{
        height: 'calc(100vh - 74px)',
        margin: (theme) => theme.spacing(-3),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Left – Order panel */}
        <Grid
          item
          xs={12}
          md={4}
          lg={3}
          sx={{
            borderRight: `1px solid ${POS_COLORS.DIVIDER}`,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <OrderPanel
            selectedCategory={state.selectedCategory}
            selectedProduct={state.selectedProduct}
            modifierGroups={state.modifierGroups}
            count={state.count}
            orderData={state.orderData}
            menuMap={menuMap}
            onRemoveItem={handleRemoveItem}
            onClearOrder={handleClearOrder}
          />
        </Grid>

        {/* Right – Menu grid */}
        <Grid
          item
          xs={12}
          md={8}
          lg={9}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: POS_COLORS.SURFACE,
          }}
        >
          {/* Action bar */}
          {state.selectedProduct && hasModifiers && (
            <ActionBar
              selectedProduct={state.selectedProduct}
              count={state.count}
              onSetCount={handleSetCount}
              onDone={handleDone}
              onCancel={handleCancel}
              onBack={handleNavigateUp}
              onSaveBack={handleSaveNavigateUp}
              isValid={isModifiersValid}
              missingGroups={missingGroups}
              isInNestedContext={isInNestedContext}
              siblingData={siblingData}
              onSwitchSiblingOption={handleSwitchSiblingOption}
            />
          )}

          {/* Scrollable menu area – at nested level only Modifier Groups (no Categories/Items) */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {!isInNestedContext && (
              <>
                <CategoriesGrid
                  menuMap={menuMap}
                  selectedCategory={state.selectedCategory}
                  onSelectCategory={handleSelectCategory}
                />

                {state.selectedCategory && (
                  <ProductsGrid
                    menuMap={menuMap}
                    selectedCategory={state.selectedCategory}
                    selectedProduct={state.selectedProduct}
                    onSelectProduct={handleSelectProduct}
                  />
                )}
              </>
            )}

            {state.selectedProduct && hasModifiers && (
              <ModifierGroupsPanel
                menuMap={menuMap}
                selectedProduct={state.selectedProduct}
                modifierGroups={state.modifierGroups}
                onUpdateModifierGroups={handleUpdateModifierGroups}
                onNavigateInto={handleNavigateInto}
                onOpenNestedModal={handleOpenNestedModal}
                currentOptionPathKey={state.currentOptionPathKey}
                pendingOptionItem={state.pendingOptionItem}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Nested Layout Modal (showNestedLayout === 'popup') */}
      <NestedLayoutModal
        open={nestedModal.open}
        onClose={handleCloseNestedModal}
        optionItem={nestedModal.optionItem}
        parentMgPathKey={nestedModal.parentMgPathKey}
        optionPathKey={nestedModal.optionPathKey}
        menuMap={menuMap}
        selectedProduct={state.selectedProduct}
        modifierGroups={state.modifierGroups}
        onUpdateModifierGroups={handleUpdateModifierGroups}
      />
    </Box>
  );
};

export default PosMenuPreview;
